import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

/**
 * Multi-popup pages component that manages multi-step popup flow
 * @extends Component
 */
class MultiPopupPagesComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    // Wait for next tick to ensure DOM is ready
    requestAnimationFrame(() => {
      this.currentPageIndex = 0;
      this.totalPages = parseInt(this.dataset.totalPages || '1', 10);
      this.answers = {}; // Store answers for each page
      
      // Get dialog component - wait for it to be defined
      this.dialogComponent = this.querySelector('dialog-component');
      
      // Get all pages
      this.pages = Array.from(this.querySelectorAll('.multi-popup-pages__page'));
      
      // Setup event listeners
      this.setupEventListeners();
      
      // Initialize first page
      if (this.pages.length > 0) {
        this.showPage(0);
        // Initialize checked states for all pages
        this.initializeCheckedStates();
      }
    });
  }

  setupEventListeners() {
    // Trigger button now uses declarative event handling (on:click="/openDialog")

    // Navigation buttons
    this.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      
      const button = target.closest('[data-action]');
      if (!(button instanceof HTMLElement)) return;

      const action = button.dataset.action;
      
      // Use currentPageIndex instead of button's pageIndex to ensure we're using the actual current page
      const currentIndex = this.currentPageIndex !== undefined ? this.currentPageIndex : 0;

      if (action === 'next') {
        this.goToNextPage(currentIndex);
      } else if (action === 'prev') {
        this.goToPreviousPage(currentIndex);
      } else if (action === 'complete') {
        this.handleCompletion();
      }
    });

    // Checkbox change handler
    this.addEventListener('change', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLInputElement)) return;
      
      if (target.type === 'checkbox' && target.closest('.multi-popup-pages__checkbox-wrapper')) {
        this.handleCheckboxChange(target);
      }
    });

    // Answer change handler
    this.addEventListener('change', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLInputElement)) return;
      
      if (target.type === 'radio' && target.closest('.multi-popup-pages__answers')) {
        this.handleAnswerChange(target);
      }
    });

    // Dialog open/close events
    if (this.dialogComponent) {
      this.dialogComponent.addEventListener('dialog:open', () => {
        this.showPage(0);
      });
      
      this.dialogComponent.addEventListener('dialog:close', () => {
        // Reset to first page when dialog closes
        this.currentPageIndex = 0;
        this.showPage(0);
      });
    }
  }

  /**
   * Opens the dialog - called by declarative event handler
   * @param {MouseEvent} event - The click event
   */
  openDialog(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Try to get dialog component if not already set
    if (!this.dialogComponent) {
      this.dialogComponent = this.querySelector('dialog-component');
    }
    
    if (!this.dialogComponent) {
      console.warn('Dialog component not found');
      return;
    }
    
    // Check if it's a DialogComponent instance
    if (this.dialogComponent instanceof DialogComponent) {
      this.dialogComponent.showDialog();
    } else if ('showDialog' in this.dialogComponent && typeof this.dialogComponent.showDialog === 'function') {
      this.dialogComponent.showDialog();
    } else {
      // Fallback: try to find and open the dialog element directly
      const dialog = this.dialogComponent.querySelector('dialog');
      if (dialog instanceof HTMLDialogElement) {
        dialog.showModal();
        // Dispatch custom event for consistency
        this.dialogComponent.dispatchEvent(new CustomEvent('dialog:open'));
      } else {
        console.warn('Dialog element not found in dialog component');
      }
    }
  }

  /**
   * @param {number} index
   */
  showPage(index) {
    if (!this.pages || index < 0 || index >= this.pages.length) return;

    // Hide all pages
    this.pages.forEach((page) => {
      page.classList.remove('multi-popup-pages__page--active');
    });

    // Show current page
    const currentPage = this.pages[index];
    if (currentPage) {
      currentPage.classList.add('multi-popup-pages__page--active');
      this.currentPageIndex = index;
      
      // Update navigation buttons for all pages
      this.pages.forEach((page, i) => {
        this.updateNavigationButtons(i);
      });

      // Update footer visibility
      this.updateFooterVisibility(index);

      // If this is a completion page, update button link based on first question answer
      const pageType = currentPage.dataset.pageType;
      if (pageType === 'completion') {
        this.updateCompletionButtonLink(currentPage);
      }
    }
  }

  /**
   * Update footer visibility based on current page
   * @param {number} pageIndex
   */
  updateFooterVisibility(pageIndex) {
    // Update footer text visibility (intro pages)
    const footers = this.querySelectorAll('.multi-popup-pages__page-footer');
    footers.forEach((footer) => {
      if (!(footer instanceof HTMLElement)) return;
      const footerPageIndex = parseInt(footer.dataset.pageIndex || '-1', 10);
      if (footerPageIndex === pageIndex) {
        footer.classList.add('multi-popup-pages__page-footer--active');
      } else {
        footer.classList.remove('multi-popup-pages__page-footer--active');
      }
    });

    // Update footer navigation visibility (question and completion pages)
    const footerNavigations = this.querySelectorAll('.multi-popup-pages__footer-navigation');
    footerNavigations.forEach((nav) => {
      if (!(nav instanceof HTMLElement)) return;
      const navPageIndex = parseInt(nav.dataset.pageIndex || '-1', 10);
      if (navPageIndex === pageIndex) {
        nav.classList.add('multi-popup-pages__footer-navigation--active');
      } else {
        nav.classList.remove('multi-popup-pages__footer-navigation--active');
      }
    });
  }

  /**
   * @param {number} [currentIndex] - Optional current index, defaults to this.currentPageIndex
   */
  goToNextPage(currentIndex) {
    if (!this.pages) return;
    const current = currentIndex !== undefined ? currentIndex : (this.currentPageIndex || 0);
    const nextIndex = current + 1;
    if (nextIndex < this.pages.length) {
      this.showPage(nextIndex);
    }
  }

  /**
   * @param {number} [currentIndex] - Optional current index, defaults to this.currentPageIndex
   */
  goToPreviousPage(currentIndex) {
    const current = currentIndex !== undefined ? currentIndex : (this.currentPageIndex || 0);
    const prevIndex = current - 1;
    if (prevIndex >= 0) {
      this.showPage(prevIndex);
    }
  }

  /**
   * @param {number} pageIndex
   */
  updateNavigationButtons(pageIndex) {
    if (!this.pages) return;
    const page = this.pages[pageIndex];
    if (!(page instanceof HTMLElement)) return;

    const pageType = page.dataset.pageType;
    
    // Update navigation buttons in footer (for question and completion pages)
    if (pageType === 'question' || pageType === 'completion') {
      const footerNav = this.querySelector(`.multi-popup-pages__footer-navigation[data-page-index="${pageIndex}"]`);
      if (footerNav instanceof HTMLElement) {
        // Update previous button
        const prevButton = footerNav.querySelector('[data-action="prev"]');
        if (prevButton instanceof HTMLButtonElement) {
          if (pageIndex === 0) {
            prevButton.disabled = true;
            prevButton.classList.add('multi-popup-pages__nav-button--disabled');
          } else {
            prevButton.disabled = false;
            prevButton.classList.remove('multi-popup-pages__nav-button--disabled');
          }
        }

        // Update next button for question pages
        if (pageType === 'question') {
          const nextButton = footerNav.querySelector('[data-action="next"]');
          if (nextButton instanceof HTMLButtonElement) {
            const hasAnswer = this.answers && pageIndex in this.answers;
            if (hasAnswer) {
              nextButton.disabled = false;
              nextButton.classList.remove('multi-popup-pages__nav-button--disabled');
            } else {
              nextButton.disabled = true;
              nextButton.classList.add('multi-popup-pages__nav-button--disabled');
            }
          }
        }
      }
    }

    // Update next button for intro pages (based on checkbox) - button is in intro-group
    if (pageType === 'intro') {
      const checkbox = page.querySelector('input[type="checkbox"]');
      const actionButton = page.querySelector('[data-action="next"]');
      if (checkbox instanceof HTMLInputElement && actionButton instanceof HTMLButtonElement) {
        const isRequired = checkbox.hasAttribute('required');
        if (isRequired) {
          actionButton.disabled = !checkbox.checked;
          if (checkbox.checked) {
            actionButton.classList.remove('multi-popup-pages__action-button--disabled');
            // 设置按钮背景颜色为 #EA580C
            actionButton.style.setProperty('background-color', '#EA580C', 'important');
            actionButton.style.setProperty('border-color', '#EA580C', 'important');
            actionButton.style.setProperty('color', '#FFFFFF', 'important');
          } else {
            actionButton.classList.add('multi-popup-pages__action-button--disabled');
            // 移除内联样式，让 CSS 处理禁用状态
            actionButton.style.removeProperty('background-color');
            actionButton.style.removeProperty('border-color');
            actionButton.style.removeProperty('color');
          }
        }
      }
    }
  }

  /**
   * @param {HTMLInputElement} checkbox
   */
  handleCheckboxChange(checkbox) {
    if (!(checkbox instanceof HTMLInputElement)) return;
    
    const page = checkbox.closest('.multi-popup-pages__page');
    if (!(page instanceof HTMLElement)) return;

    const pageIndex = parseInt(page.dataset.pageIndex || '0', 10);
    const pageType = page.dataset.pageType;

    if (pageType === 'intro') {
      const actionButton = page.querySelector('[data-action="next"]');
      if (actionButton instanceof HTMLButtonElement) {
        if (checkbox.checked) {
          actionButton.disabled = false;
          actionButton.classList.remove('multi-popup-pages__action-button--disabled');
          // 设置按钮背景颜色为 #EA580C
          actionButton.style.setProperty('background-color', '#EA580C', 'important');
          actionButton.style.setProperty('border-color', '#EA580C', 'important');
          actionButton.style.setProperty('color', '#FFFFFF', 'important');
        } else {
          actionButton.disabled = true;
          actionButton.classList.add('multi-popup-pages__action-button--disabled');
          // 移除内联样式，让 CSS 处理禁用状态
          actionButton.style.removeProperty('background-color');
          actionButton.style.removeProperty('border-color');
          actionButton.style.removeProperty('color');
        }
      }

      // 更新标题（如果设置了选中后的标题）
      const pageTitle = page.querySelector('.multi-popup-pages__page-title');
      if (pageTitle instanceof HTMLElement) {
        const originalTitle = pageTitle.dataset.originalTitle;
        const checkedTitle = pageTitle.dataset.checkedTitle;
        
        if (checkedTitle && checkedTitle.trim() !== '') {
          if (checkbox.checked) {
            pageTitle.textContent = checkedTitle;
          } else {
            pageTitle.textContent = originalTitle || '';
          }
        }
      }
    }
  }

  /**
   * @param {HTMLInputElement} radio
   */
  handleAnswerChange(radio) {
    if (!(radio instanceof HTMLInputElement)) return;
    
    const page = radio.closest('.multi-popup-pages__page');
    if (!(page instanceof HTMLElement)) return;

    const pageIndex = parseInt(page.dataset.pageIndex || '0', 10);
    const pageType = page.dataset.pageType;

    if (pageType === 'question') {
      // Store answer
      if (!this.answers) {
        this.answers = {};
      }
      /** @type {Record<number, string>} */ (this.answers)[pageIndex] = radio.value;

      // Update next button in footer navigation
      const footerNav = this.querySelector(`.multi-popup-pages__footer-navigation[data-page-index="${pageIndex}"]`);
      if (footerNav instanceof HTMLElement) {
        const nextButton = footerNav.querySelector('[data-action="next"]');
        if (nextButton instanceof HTMLButtonElement) {
          nextButton.disabled = false;
          nextButton.classList.remove('multi-popup-pages__nav-button--disabled');
        }
      }

      // Update all answer buttons to reflect their checked state
      const allRadios = page.querySelectorAll('input[type="radio"]');
      allRadios.forEach((radioInput) => {
        if (radioInput instanceof HTMLInputElement) {
          const answerButton = radioInput.nextElementSibling;
          if (answerButton instanceof HTMLElement && answerButton.classList.contains('multi-popup-pages__answer-button')) {
            // Add/remove checked class for better CSS targeting
            if (radioInput.checked) {
              answerButton.classList.add('multi-popup-pages__answer-button--checked');
              // Ensure background color is applied - check if custom color is set via CSS variable
              const computedStyle = getComputedStyle(answerButton);
              const customBgColor = computedStyle.getPropertyValue('--option-bg-color').trim();
              if (!customBgColor) {
                // No custom color set, use default checked color
                // Set via setProperty with important flag (using setAttribute as fallback)
                answerButton.style.setProperty('background-color', '#EA580C', 'important');
                answerButton.style.setProperty('border-color', '#EA580C', 'important');
                answerButton.style.setProperty('color', '#FFFFFF', 'important');
              } else {
                // Custom color is set, remove inline styles to let CSS variable handle it
                answerButton.style.removeProperty('background-color');
                answerButton.style.removeProperty('border-color');
                answerButton.style.removeProperty('color');
              }
            } else {
              answerButton.classList.remove('multi-popup-pages__answer-button--checked');
              // Remove inline styles to let CSS handle the styling
              answerButton.style.removeProperty('background-color');
              answerButton.style.removeProperty('border-color');
              answerButton.style.removeProperty('color');
            }
          }
        }
      });

      // If there's a completion page visible, update its button link
      const currentPage = this.pages[this.currentPageIndex];
      if (currentPage instanceof HTMLElement) {
        const currentPageType = currentPage.dataset.pageType;
        if (currentPageType === 'completion') {
          this.updateCompletionButtonLink(currentPage);
        }
      }
    }
  }

  /**
   * Initialize checked states for all answer buttons
   */
  initializeCheckedStates() {
    if (!this.pages) return;
    
    this.pages.forEach((page) => {
      const allRadios = page.querySelectorAll('input[type="radio"]');
      allRadios.forEach((radioInput) => {
        if (radioInput instanceof HTMLInputElement) {
          const answerButton = radioInput.nextElementSibling;
          if (answerButton instanceof HTMLElement && answerButton.classList.contains('multi-popup-pages__answer-button')) {
            if (radioInput.checked) {
              answerButton.classList.add('multi-popup-pages__answer-button--checked');
              // Ensure background color is applied - check if custom color is set via CSS variable
              const computedStyle = getComputedStyle(answerButton);
              const customBgColor = computedStyle.getPropertyValue('--option-bg-color').trim();
              if (!customBgColor) {
                // No custom color set, use default checked color
                answerButton.style.setProperty('background-color', '#EA580C', 'important');
                answerButton.style.setProperty('border-color', '#EA580C', 'important');
                answerButton.style.setProperty('color', '#FFFFFF', 'important');
              }
            } else {
              answerButton.classList.remove('multi-popup-pages__answer-button--checked');
            }
          }
        }
      });
    });
  }

  /**
   * Update completion button link based on first question answer
   * @param {HTMLElement} completionPage
   */
  updateCompletionButtonLink(completionPage) {
    const completionButton = completionPage.querySelector('[data-action="complete"]');
    if (!(completionButton instanceof HTMLElement)) return;

    // 首先检查是否所有问题都选择了相同的答案
    const allSameLinkMapping = completionButton.dataset.allSameLinkMapping;
    if (allSameLinkMapping && this.answers) {
      const allAnswers = Object.values(this.answers);
      if (allAnswers.length > 0) {
        // 检查所有答案是否相同
        const firstAnswer = String(allAnswers[0]).trim();
        const allSame = allAnswers.every(answer => String(answer).trim() === firstAnswer);
        
        if (allSame) {
          // 所有答案都相同，使用对应的链接
          const mappings = allSameLinkMapping.split('|');
          for (const mapping of mappings) {
            const [answer, url] = mapping.split(':');
            if (answer && answer.trim() === firstAnswer) {
              completionButton.dataset.targetUrl = url || '';
              return;
            }
          }
        }
      }
    }

    // 如果不是所有答案都相同，则使用第六题的逻辑
    const sixthQuestionIndex = completionButton.dataset.sixthQuestionIndex;
    const firstQuestionIndex = completionButton.dataset.firstQuestionIndex;
    const questionIndex = sixthQuestionIndex || firstQuestionIndex;
    const linkMapping = completionButton.dataset.linkMapping;

    if (!questionIndex || !linkMapping) {
      completionButton.dataset.targetUrl = '';
      return;
    }

    // Get the answer from sixth question (or first question if sixth doesn't exist)
    const questionAnswer = this.answers && this.answers[parseInt(questionIndex, 10)];
    if (!questionAnswer) {
      // No answer yet, disable link functionality
      completionButton.dataset.targetUrl = '';
      return;
    }

    // Parse link mapping: "Yes:url1|Sometimes:url2|No:url3"
    const mappings = linkMapping.split('|');
    for (const mapping of mappings) {
      const [answer, url] = mapping.split(':');
      if (answer && answer.trim() === questionAnswer.trim()) {
        completionButton.dataset.targetUrl = url || '';
        return;
      }
    }

    // No matching link found
    completionButton.dataset.targetUrl = '';
  }

  handleCompletion() {
    // Handle completion action
    // You can customize this to redirect, show results, etc.
    console.log('Test completed!', this.answers);
    
    // Get the completion button to check for target URL
    const currentPage = this.pages[this.currentPageIndex];
    if (!(currentPage instanceof HTMLElement)) return;

    const completionButton = currentPage.querySelector('[data-action="complete"]');
    if (completionButton instanceof HTMLElement) {
      const targetUrl = completionButton.dataset.targetUrl;
      if (targetUrl && targetUrl.trim() !== '') {
        // Redirect to the target URL
        window.location.href = targetUrl;
        return;
      }
    }
    
    // Optionally close the dialog if no redirect
    // if (this.dialogComponent) {
    //   this.dialogComponent.closeDialog();
    // }
  }
}

if (!customElements.get('multi-popup-pages-component')) {
  customElements.define('multi-popup-pages-component', MultiPopupPagesComponent);
}

// Support for external popup triggers
/**
 * @param {string} sectionId
 * @returns {MultiPopupPagesComponent | null}
 */
function findPopupComponent(sectionId) {
  const normalizedId = sectionId.replace(/^shopify-section-/, '').trim();
  if (!normalizedId) return null;

  /** @param {string} candidate */
  const matchesSectionId = (candidate) => {
    if (!candidate) return false;
    const normalizedCandidate = candidate.replace(/^shopify-section-/, '').trim();
    if (normalizedCandidate === normalizedId) return true;
    if (normalizedCandidate.endsWith(`__${normalizedId}`)) return true;
    if (normalizedId.endsWith(`__${normalizedCandidate}`)) return true;
    return false;
  };

  let popupComponent = document.querySelector(
    `multi-popup-pages-component[data-section-id="${sectionId}"]`
  );

  if (!popupComponent) {
    popupComponent = document.querySelector(
      `multi-popup-pages-component[data-section-id="${normalizedId}"]`
    );
  }

  if (!popupComponent) {
    popupComponent = document.querySelector(
      `multi-popup-pages-component[data-section-id="shopify-section-${normalizedId}"]`
    );
  }

  if (!popupComponent) {
    const sectionElement = document.getElementById(sectionId);
    if (sectionElement) {
      popupComponent = sectionElement.querySelector('multi-popup-pages-component');
    }
  }

  if (!popupComponent) {
    const sectionElement = document.getElementById(`shopify-section-${normalizedId}`);
    if (sectionElement) {
      popupComponent = sectionElement.querySelector('multi-popup-pages-component');
    }
  }

  if (!popupComponent) {
    const sectionElement = document.querySelector(
      `[id="shopify-section-${normalizedId}"], [id$="__${normalizedId}"]`
    );
    if (sectionElement) {
      popupComponent = sectionElement.querySelector('multi-popup-pages-component');
    }
  }

  if (!popupComponent) {
    const allComponents = document.querySelectorAll('multi-popup-pages-component');
    for (const component of allComponents) {
      const componentSectionId = component.getAttribute('data-section-id');
      if (matchesSectionId(componentSectionId || '')) {
        popupComponent = component;
        break;
      }

      const parentSection = component.closest('[id^="shopify-section-"]');
      if (parentSection instanceof HTMLElement && matchesSectionId(parentSection.id)) {
        popupComponent = component;
        break;
      }
    }
  }

  return isMultiPopupComponent(popupComponent) ? popupComponent : null;
}

/**
 * @param {Element | null | undefined} component
 * @returns {component is MultiPopupPagesComponent}
 */
function isMultiPopupComponent(component) {
  return (
    component instanceof HTMLElement &&
    typeof /** @type {MultiPopupPagesComponent} */ (component).openDialog === 'function'
  );
}

let popupTriggersInitialized = false;

function setupPopupTriggers() {
  if (popupTriggersInitialized) return;
  popupTriggersInitialized = true;

  document.addEventListener(
    'click',
    (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const trigger = target.closest('[data-popup-section-id]');
      if (!(trigger instanceof HTMLElement)) return;

      const sectionId = trigger.dataset.popupSectionId?.trim();
      if (!sectionId) return;

      const popupComponent = findPopupComponent(sectionId);
      if (!popupComponent) {
        console.warn(`Multi-popup component not found for section ID: ${sectionId}`);
        console.warn('Available components:', document.querySelectorAll('multi-popup-pages-component'));
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      popupComponent.openDialog(e);
    },
    true
  );
}

// Setup triggers when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupPopupTriggers);
} else {
  setupPopupTriggers();
}

// Also setup triggers when new content is loaded (for dynamic sections)
if (typeof document.addEventListener !== 'undefined') {
  // Listen for section:load event (Shopify theme editor)
  document.addEventListener('shopify:section:load', setupPopupTriggers);
  document.addEventListener('shopify:section:reorder', setupPopupTriggers);
}

